import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import { saveAs } from 'file-saver';
import toPDF from 'react-to-pdf';

import BACKEND_URL from '../../../vnog/config';
import AIOperatorAnalytics from './AIOperatorAnalytics';
import CharacteristicAnalytics from './CharacteristicAnalytics';
import DocumentSearchAnalytics from './DocumentSearchAnalytics';
import { AnalyticsPayload } from './types';

type SubTab = 'characteristics' | 'ai_operator' | 'document_search';

const SUB_TABS: { id: SubTab; label: string }[] = [
  { id: 'characteristics', label: 'Characteristics' },
  { id: 'ai_operator', label: 'AI Operator' },
  { id: 'document_search', label: 'Document Search' },
];

const REPORT_ROOT_ID = 'vnog-analytics-report-root';

const AnalyticsTab: React.FC<{ onLoadingChange?: (loading: boolean) => void }> = ({ onLoadingChange }) => {
  const [payload, setPayload] = useState<AnalyticsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [subTab, setSubTab] = useState<SubTab>('characteristics');
  const [pdfRendering, setPdfRendering] = useState(false);
  const fullReportRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchLatest();
  }, []);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const fetchLatest = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/vnog/admin/analytics/latest/`, {
        credentials: 'include',
      });
      if (!response.ok) {
        toast.error('Failed to fetch analytics');
        return;
      }
      const data = (await response.json()) as AnalyticsPayload;
      setPayload(data);
    } catch (err) {
      console.error('analytics fetch error', err);
      toast.error('Error fetching analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleRecompute = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const csrf = Cookies.get('csrftoken') || '';
      const response = await fetch(`${BACKEND_URL}/vnog/admin/analytics/recompute/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRFToken': csrf },
      });
      if (!response.ok) {
        toast.error('Failed to enqueue recompute');
        return;
      }
      toast.success('Recompute scheduled. Reload in a few moments to see fresh data.');
    } catch (err) {
      console.error('analytics recompute error', err);
      toast.error('Error scheduling recompute');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDownloadJSON = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/vnog/admin/analytics/export/?format=json`, {
        credentials: 'include',
      });
      if (!response.ok) {
        toast.error('Failed to download report (JSON)');
        return;
      }
      const blob = await response.blob();
      saveAs(blob, `vnog-feedback-analytics-${new Date().toISOString().slice(0, 10)}.json`);
    } catch (err) {
      console.error(err);
      toast.error('Error downloading JSON report');
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/vnog/admin/analytics/export/?format=csv`, {
        credentials: 'include',
      });
      if (!response.ok) {
        toast.error('Failed to download report (CSV zip)');
        return;
      }
      const blob = await response.blob();
      saveAs(blob, `vnog-feedback-analytics-${new Date().toISOString().slice(0, 10)}.zip`);
    } catch (err) {
      console.error(err);
      toast.error('Error downloading CSV report');
    }
  };

  const handleDownloadPDF = async () => {
    if (!payload) {
      toast.warn('Nothing to export yet.');
      return;
    }
    setPdfRendering(true);
    await new Promise(resolve => setTimeout(resolve, 250));
    try {
      await toPDF(() => document.getElementById(REPORT_ROOT_ID), {
        filename: `vnog-feedback-analytics-${new Date().toISOString().slice(0, 10)}.pdf`,
        page: { margin: 20, format: 'a4' },
      });
    } catch (err) {
      console.error('analytics pdf error', err);
      toast.error('Error generating PDF report');
    } finally {
      setPdfRendering(false);
    }
  };

  const lastUpdatedLabel = useMemo(() => {
    const ts = payload?.snapshot?.completed_at || payload?.snapshot?.started_at;
    if (!ts) return 'never';
    return new Date(ts).toLocaleString();
  }, [payload?.snapshot]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-gray-600">Loading analytics\u2026</div>
      </div>
    );
  }

  if (!payload) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="text-center text-gray-600">No analytics data could be loaded.</div>
      </div>
    );
  }

  const noSnapshotYet =
    payload?.snapshot?.status !== 'success' &&
    Object.keys(payload?.characteristics || {}).length === 0 &&
    Object.keys(payload?.ai_operator || {}).length === 0 &&
    Object.keys(payload?.document_search || {}).length === 0;

  const generatedAt = payload?.snapshot?.completed_at || payload?.snapshot?.started_at || null;

  const renderSection = (id: SubTab) => {
    if (id === 'characteristics') {
      return (
        <CharacteristicAnalytics
          data={payload.characteristics || {}}
          generatedAt={generatedAt}
        />
      );
    }
    if (id === 'ai_operator') {
      return (
        <AIOperatorAnalytics
          data={payload.ai_operator || {}}
          generatedAt={generatedAt}
        />
      );
    }
    return (
      <DocumentSearchAnalytics
        data={payload.document_search || {}}
        generatedAt={generatedAt}
      />
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-baseline gap-3">
          <h2 className="text-2xl font-semibold">Feedback Analytics</h2>
          <span className="text-xs text-gray-500">
            last updated {lastUpdatedLabel}
            {payload?.snapshot?.status && payload.snapshot.status !== 'success' && (
              <span className="ml-2 px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                {payload.snapshot.status}
              </span>
            )}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
            onClick={fetchLatest}
            disabled={loading}
          >
            Refresh
          </button>
          <button
            className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            onClick={handleRecompute}
            disabled={refreshing}
          >
            {refreshing ? 'Scheduling\u2026' : 'Recompute now'}
          </button>
          <button
            className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
            onClick={handleDownloadPDF}
            disabled={pdfRendering}
          >
            {pdfRendering ? 'Building PDF\u2026' : 'Download PDF'}
          </button>
          <button
            className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
            onClick={handleDownloadJSON}
          >
            Download JSON
          </button>
          <button
            className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50"
            onClick={handleDownloadCSV}
          >
            Download CSV
          </button>
        </div>
      </div>

      {noSnapshotYet && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 mb-4">
          {payload.message || 'No analytics snapshot has been generated yet. Click "Recompute now" to schedule one.'}
        </div>
      )}

      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Analytics sub-tabs">
          {SUB_TABS.map(tab => (
            <button
              key={tab.id}
              className={`whitespace-nowrap py-2 px-1 border-b-2 text-sm font-medium ${
                subTab === tab.id
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              onClick={() => setSubTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div>{renderSection(subTab)}</div>

      <div
        id={REPORT_ROOT_ID}
        ref={fullReportRef}
        style={{
          position: 'absolute',
          left: pdfRendering ? '0' : '-99999px',
          top: pdfRendering ? '0' : '-99999px',
          width: '1100px',
          background: '#ffffff',
          padding: '24px',
          zIndex: pdfRendering ? 50 : -1,
          opacity: pdfRendering ? 1 : 0,
          pointerEvents: 'none',
        }}
        aria-hidden={!pdfRendering}
      >
        <h1 className="text-2xl font-bold mb-2">VNOG Feedback Analytics Report</h1>
        <p className="text-xs text-gray-600 mb-6">Snapshot generated {lastUpdatedLabel}</p>
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">Characteristics</h2>
          <CharacteristicAnalytics data={payload.characteristics || {}} generatedAt={generatedAt} />
        </section>
        <section className="mb-8">
          <h2 className="text-xl font-semibold mb-3">AI Operator</h2>
          <AIOperatorAnalytics data={payload.ai_operator || {}} generatedAt={generatedAt} />
        </section>
        <section>
          <h2 className="text-xl font-semibold mb-3">Document Search</h2>
          <DocumentSearchAnalytics data={payload.document_search || {}} generatedAt={generatedAt} />
        </section>
      </div>
    </div>
  );
};

export default AnalyticsTab;
